import { lazy, Suspense } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { paths, rp } from '@/routes/paths'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import { StepUpProvider } from '@/context/StepUpContext'
import { StepUpProvider as MfaStepUpProvider } from '@/components/mfa'
import { BrandProvider } from '@/context/BrandContext'
import { ToastProvider } from '@/components/ui/toast'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ErrorFallback } from '@/components/ErrorFallback'
import { Sentry } from '@/lib/observability/sentry'
import { PersonaAppShell } from '@/app/PersonaAppShell'
import { PlatformStatusBanner } from '@/components/status/PlatformStatusBanner'
import { Navbar } from '@/components/layout/Navbar'
import { Footer } from '@/components/layout/Footer'
import { ListingsPage } from '@/pages/ListingsPage'
import { ManualListingComposerPage } from '@/pages/agent/listings/ManualListingComposerPage'
import { PropertyDispositionPage } from '@/pages/agent/listings/PropertyDispositionPage'
import { PublishOutcomePage } from '@/pages/agent/PublishOutcomePage'
import { PortalTrackerPage } from '@/pages/agent/PortalTrackerPage'
import { ListingProfilePage } from '@/pages/ListingProfilePage'
import { AgentProfilePage } from '@/pages/AgentProfilePage'
import { RegisterPage } from '@/pages/RegisterPage'
import { AgentDashboardPage } from '@/pages/AgentDashboardPage'
import { AgentPricingPage } from '@/pages/AgentPricingPage'
import { PriceBriefPage } from '@/pages/agent/pricing/PriceBriefPage'
import { AgencyPricingPage } from '@/pages/AgencyPricingPage'
import { AgencyComparablesPage } from '@/pages/agency/pricing/AgencyComparablesPage'
import { AgencyCreditsLayout } from '@/pages/agency/credits/AgencyCreditsLayout'
import { AgencyCreditsWalletPage } from '@/pages/agency/credits/AgencyCreditsWalletPage'
import { AgencyFeatureQuotasPage } from '@/pages/agency/credits/AgencyFeatureQuotasPage'
import { AgencyBulkPriceAdjustPage } from '@/pages/agency/pricing/AgencyBulkPriceAdjustPage'
import {
  BadComparableReportPage,
  PriceReportPage,
  ComparableReportOutcomePage,
  PriceReportOutcomePage,
  MySubmittedReportsPage,
} from '@/pages/agent/reports'
import { AgentReviewsPage } from '@/pages/agent/reviews/AgentReviewsPage'
import { CalendarDiaryPage } from '@/pages/agent/calendar/CalendarDiaryPage'
import { AreaXRayPage } from '@/pages/agent/areas/AreaXRayPage'
import { InboxPage, InboxConversationPage } from '@/pages/InboxPage'
import { TasksPage } from '@/pages/TasksPage'
import { ContactsPage } from '@/pages/ContactsPage'
import { ContactDetailPage } from '@/pages/ContactDetailPage'
import { ContactFormPage } from '@/pages/ContactFormPage'
import { RelationshipsEditorPage } from '@/pages/agent/contacts/RelationshipsEditorPage'
import { RelationshipConsentPage } from '@/pages/public/RelationshipConsentPage'
import { PublicBookingPage } from '@/pages/public/PublicBookingPage'
import { OpportunitiesPage } from '@/pages/OpportunitiesPage'
import { OpportunityDetailPage } from '@/pages/agent/OpportunityDetailPage'
import { AgentPublicProfileEditorPage } from '@/pages/agent/AgentPublicProfileEditorPage'
import { CrmAnalyticsPage } from '@/pages/CrmAnalyticsPage'
import { MarketingDashboardPage } from '@/pages/MarketingDashboardPage'
import { CampaignsPage } from '@/pages/CampaignsPage'
import { CampaignBuilderPage } from '@/pages/CampaignBuilderPage'
import { JourneyRunInspectorPage } from '@/pages/JourneyRunInspectorPage'
import { AudiencesPage } from '@/pages/AudiencesPage'
import { AudienceBuilderPage } from '@/pages/AudienceBuilderPage'
import { ExperimentsPage } from '@/pages/ExperimentsPage'
import { ExperimentBuilderPage } from '@/pages/ExperimentBuilderPage'
import { ExperimentResultsPage } from '@/pages/ExperimentResultsPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { AccountRecoveryPage } from '@/pages/AccountRecoveryPage'
import { AccountRecoveryCompletePage } from '@/pages/AccountRecoveryCompletePage'
import { ScheduledDeletionConfirmationPage } from '@/pages/public/ScheduledDeletionConfirmationPage'
import { AgencyManagementPage } from '@/pages/AgencyManagementPage'
import { WhiteLabelBuilderPage } from '@/pages/WhiteLabelBuilderPage'
import { AgencyWhiteLabelAnalyticsPage } from '@/pages/agency/white-label/AgencyWhiteLabelAnalyticsPage'
import { WidgetBuilderPage } from '@/pages/WidgetBuilderPage'
import { IntegrationSettingsPage } from '@/pages/IntegrationSettingsPage'
import { SocialChannelsPage } from '@/pages/SocialChannelsPage'
import { PaidCampaignBuilderPage } from '@/pages/PaidCampaignBuilderPage'
import { NeighborhoodValuatorPage } from '@/pages/NeighborhoodValuatorPage'
import { SellerPerformanceReportPage } from '@/pages/agent/listings/SellerPerformanceReportPage'
import { SellerReportSharePage } from '@/pages/public/SellerReportSharePage'
import { HistoricalTransactionsPage } from '@/pages/HistoricalTransactionsPage'
import { CommandCenterPage } from '@/pages/CommandCenterPage'
import { RoutingSettingsPage } from '@/pages/RoutingSettingsPage'
import { MessageTemplatesPage } from '@/pages/MessageTemplatesPage'
import { MessageTemplateEditorPage } from '@/pages/agent/MessageTemplateEditorPage'
// Platform-notifications admin — separate from the agent-level
// /message-templates page above. Lives at /admin/message-templates.
import { MessageTemplatesPage as PlatformMessageTemplatesPage } from '@/pages/admin/platform-templates/MessageTemplatesPage'
import { TemplateEditPage as PlatformTemplateEditPage } from '@/pages/admin/platform-templates/TemplateEditPage'
import { AdminWhatsAppListingsPage } from '@/pages/admin/whatsapp-listings/AdminWhatsAppListingsPage'
import { WhatsAppAuditLogPage } from '@/pages/admin/whatsapp-listings/WhatsAppAuditLogPage'
import { NotificationDeadLetterPage } from '@/pages/admin/notifications/NotificationDeadLetterPage'
import { AgencyWhatsAppListingsPage } from '@/pages/agency/whatsapp-listings/AgencyWhatsAppListingsPage'
import { ApplicationsQueuePage } from '@/pages/agency/ApplicationsQueuePage'
import { ApplicationDetailPage } from '@/pages/agency/ApplicationDetailPage'
import { AgencyOwnershipTransferInitiatorPage } from '@/pages/agency/AgencyOwnershipTransferInitiatorPage'
import { AgencyOwnershipTransferAcceptPage } from '@/pages/agency/AgencyOwnershipTransferAcceptPage'
import { AgencyPublicProfileSettingsPage } from '@/pages/agency/AgencyPublicProfileSettingsPage'
import { OwnershipTransferOutcomePage } from '@/pages/agent/OwnershipTransferOutcomePage'
import { AgencySettingsHomePage } from '@/pages/agency/settings/AgencySettingsHomePage'
import { RolesOverviewPage } from '@/pages/agency/settings/RolesOverviewPage'
import { RolePermissionsDetailPage } from '@/pages/agency/settings/RolePermissionsDetailPage'
import { AuditLogPage } from '@/pages/admin/audit/AuditLogPage'
import { AuditRetentionPolicyPage } from '@/pages/admin/audit/AuditRetentionPolicyPage'
import { AgencySecurityPolicyPage } from '@/pages/agency/settings/AgencySecurityPolicyPage'
import { AgencyBrandingPage } from '@/pages/agency/settings/AgencyBrandingPage'
import { AgencyContactPolicyPage } from '@/pages/agency/settings/AgencyContactPolicyPage'
import { AgencyRoutingRulesPage } from '@/pages/agency/routing/AgencyRoutingRulesPage'
import { AgencyRoutingRuleEditorPage } from '@/pages/agency/routing/AgencyRoutingRuleEditorPage'
import { AgencyTemplatesListPage } from '@/pages/agency/templates/AgencyTemplatesListPage'
import { AgencyTemplateEditorPage } from '@/pages/agency/templates/AgencyTemplateEditorPage'
import { AgencyWhiteLabelCopyPage } from '@/pages/agency/white-label/AgencyWhiteLabelCopyPage'
import { AgencyDeleteAgencyPage } from '@/pages/agency/settings/AgencyDeleteAgencyPage'
import { AgencyCreditAllocationRulesPage } from '@/pages/agency/credits/AgencyCreditAllocationRulesPage'
import { AgencyOnboardingPage } from '@/pages/agency/AgencyOnboardingPage'
import { LeadConversionFunnelPage } from '@/pages/agency/reports/LeadConversionFunnelPage'
import { RevenueAttributionReportPage } from '@/pages/agency/reports/RevenueAttributionReportPage'
import { AgencyReportsHomePage } from '@/pages/agency/reports/AgencyReportsHomePage'
import { AgencyListingsPerformanceReportPage } from '@/pages/agency/reports/AgencyListingsPerformanceReportPage'
import { AgencyAgentLeaderboardPage } from '@/pages/agency/reports/AgencyAgentLeaderboardPage'
import { AgencyCampaignPerformanceReportPage } from '@/pages/agency/reports/AgencyCampaignPerformanceReportPage'
import { AgencyAttributionPerformancePage } from '@/pages/agency/reports/AgencyAttributionPerformancePage'
import { ContentCalendarPage } from '@/pages/ContentCalendarPage'
import { AgentWhatsAppListingsPage } from '@/pages/agent/whatsapp-listings/AgentWhatsAppListingsPage'
import { WhatsAppIntakeSettingsPage } from '@/pages/agent/whatsapp-listings/WhatsAppIntakeSettingsPage'
import { ApplicationOutcomePage } from '@/pages/agent/ApplicationOutcomePage'
import { PortalSubmitPage } from '@/pages/agent/PortalSubmitPage'
import { whatsappIntakeRoutes } from '@/pages/agent/whatsapp-intake/routes'
import { AdminAreasPage } from '@/pages/admin/areas/AdminAreasPage'
import { AreaSignalsReviewPage } from '@/pages/admin/areas/AreaSignalsReviewPage'
import { AdminAreaDetailPage } from '@/pages/admin/areas/AdminAreaDetailPage'
import { AdminScoringPage } from '@/pages/admin/scoring/AdminScoringPage'
import { AiConfigsPage } from '@/pages/admin/scoring/AiConfigsPage'
import { CommentClassifierPage } from '@/pages/admin/comment-classifier/CommentClassifierPage'
import { GoogleUsagePage } from '@/pages/admin/google/GoogleUsagePage'
import { PricingAdminPage } from '@/pages/admin/pricing/PricingAdminPage'
import { CanonicalResolutionQueuePage } from '@/pages/admin/pricing/CanonicalResolutionQueuePage'
import { CanonicalResolutionDetailPage } from '@/pages/admin/pricing/CanonicalResolutionDetailPage'
import { PortalModerationQueuePage } from '@/pages/admin/PortalModerationQueuePage'
import {
  PortalRegistryListPage,
  PortalDetailPage,
  PortalActivationHistoryPage,
} from '@/pages/admin/portals'
import { MarketsAdminPage } from '@/pages/admin/markets/MarketsAdminPage'
import { StrategyConfigAdminPage } from '@/pages/admin/strategy/StrategyConfigAdminPage'
import {
  PackageListPage,
  PackageEditPage,
  PackageApprovalQueuePage,
  PackageApprovalDetailPage,
  PackageVersionHistoryPage,
  PackageVersionReadOnlyPage,
} from '@/pages/admin/packages'
import {
  BadComparableQueuePage,
  BadComparableDetailPage,
  PriceReportQueuePage,
  PriceReportDetailPage,
} from '@/pages/admin/valuation'
import {
  ApprovalsPage, ApprovalAuditTrailPage, AccountingPeriodDetailPage, AccountingPeriodsPage, AdvanceDunningStagePage, AuditPage, BillingPeriodClosePage, ConfigurationPage, ContractDetailPage, ContractsPage, CreditLotsPage,
  ContractVersionEditorPage, CreditFinMirrorPage, CreditJanitorPage, CreditsPage, CureDunningCasePage,
  DunningCaseDetailPage, DunningCasesPage, ExceptionsPage, FacilitiesPage, FeatureRegistryPage, HoldsPage, InvoicesPage, OverviewPage,
  ExceptionDetailPage,
  PackageApprovalPage, PackageDetailPage, PackagesPage, PackageVersionEditor,
  PaymentsPage, PriceDetailPage, PricingPage as FinPricingPage, ReconciliationPage, ReconciliationRunDetailPage,
  SubscriptionDetailPage,
  SubscriptionsPage, TenantDetailPage, TenantsPage, UsagePage, VendorCostsPage, VendorDetailPage, VendorStatementDetailPage, WriteOffDunningCasePage,
} from '@/pages/admin/fin'
import { NotificationPreferencesPage } from '@/pages/NotificationPreferencesPage'
import { NotificationsInboxPage } from '@/pages/agent/NotificationsInboxPage'
import { mfaRoutes, mfaSettingsChildRoutes } from '@/pages/security/mfa/routes'
import { InspectorPage } from '@/pages/inspector/InspectorPage'
import { InspectorSubmitPage } from '@/pages/inspector/InspectorSubmitPage'
import { AreaProfilePage } from '@/pages/AreaProfilePage'
import { PublicAgencyPage } from '@/pages/PublicAgencyPage'
import { PublicAgentPortfolioPage } from '@/pages/PublicAgentPortfolioPage'
import { PublicWhiteLabelSitePage } from '@/pages/PublicWhiteLabelSitePage'
import { PublicWhiteLabelPropertyPage } from '@/pages/PublicWhiteLabelPropertyPage'
import { PublicAgencyApplyPage } from '@/pages/PublicAgencyApplyPage'
import { TermsPage } from '@/pages/TermsPage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { TrustCentrePage } from '@/pages/TrustCentrePage'
import { TrustDisclosurePage } from '@/pages/TrustDisclosurePage'
import { TrustNoticeActionPage } from '@/pages/TrustNoticeActionPage'
import { TrustCentreAdminPage } from '@/pages/admin/TrustCentreAdminPage'
import { RefundPolicyPage } from '@/pages/RefundPolicyPage'
import { CookieNoticePage } from '@/pages/CookieNoticePage'
import { PlansPage } from '@/pages/PlansPage'
import { MySubscriptionPage } from '@/pages/MySubscriptionPage'
import { MyCreditsPage } from '@/pages/MyCreditsPage'
import { MyCreditNotesPage } from '@/pages/MyCreditNotesPage'
import { MyInvoicesPage } from '@/pages/MyInvoicesPage'
import { ComponentInventoryPage } from '@/pages/dev/ComponentInventory'
import { AccountRecoveryQueuePage } from '@/pages/admin/AccountRecoveryQueuePage'
import { AccountRecoveryDetailPage } from '@/pages/admin/AccountRecoveryDetailPage'
import { settingsRoutes } from '@/pages/settings/routes'
import { SettingsPage } from '@/pages/SettingsPage'
import { onboardingRoutes } from '@/pages/agent/onboarding/routes'
import { activationRoutes } from '@/pages/agent/activation/routes'

const WhatsAppIntakeAnalyticsPage = lazy(
  () => import('@/pages/agent/whatsapp-listings/WhatsAppIntakeAnalyticsPage'),
)

/** Auth / marketing surfaces that own their own chrome (no app shell / Navbar). */
const BARE_CHROME_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/account-recovery',
  '/account/scheduled-deletion',
  '/register',
  '/site/',
  '/public/',
  '/agencies/',
  '/join/',
  '/terms',
  '/privacy',
  '/refund',
  '/cookies',
  // Public Trust Centre. Matches /trust and /trust/*, but NOT /admin/trust,
  // which is an authed admin surface and keeps the app shell.
  '/trust',
  '/onboarding',
  '/activate',
  '/r/',
] as const

function usesBareChrome(pathname: string): boolean {
  return BARE_CHROME_PREFIXES.some((prefix) =>
    prefix.endsWith('/') ? pathname.startsWith(prefix) : pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
}

function AppRoutes() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
    >
    <Routes>
      <Route path={rp('/')} element={<Navigate to={paths.dashboard} replace />} />
      <Route path={rp('/dashboard')} element={<AgentDashboardPage />} />
      <Route path={rp('/listings')} element={<ListingsPage />} />
      <Route path={rp('/listings/new')} element={<ManualListingComposerPage />} />
      <Route path={rp('/listings/:id/edit')} element={<ManualListingComposerPage />} />
      <Route path={rp('/listings/:id/disposition')} element={<PropertyDispositionPage />} />
      <Route path={rp('/listings/:id')} element={<ListingProfilePage />} />
      <Route path={rp('/listings/:id/portals/submit')} element={<PortalSubmitPage />} />
      <Route path={rp('/listings/:id/neighborhood-valuator')} element={<NeighborhoodValuatorPage />} />
      <Route path={rp('/listings/:id/report')} element={<SellerPerformanceReportPage />} />
      {/* AGT-PUB-003 / AGT-PUB-005 — WF-03 publish outcome receipt */}
      <Route path={rp('/publish/outcome/:id')} element={<PublishOutcomePage />} />
      {/* AGT-PUB-006 ledger + receipt deep-link */}
      <Route path={rp('/publishing/receipts/:id')} element={<PublishOutcomePage />} />
      <Route path={rp('/publishing/tracker')} element={<PortalTrackerPage />} />
      <Route path={rp('/calendar')} element={<ContentCalendarPage />} />
      {/* AGT-APP-001 — must precede /agent/:id so "profile" is not captured as an id */}
      <Route path={rp('/agent/profile')} element={<AgentPublicProfileEditorPage />} />
      <Route path={rp('/agent/:id')} element={<AgentProfilePage />} />
      <Route path={rp('/register')} element={<RegisterPage />} />
      <Route path={rp('/agent/pricing')} element={<AgentPricingPage />} />
      {/* Issue 552 redesign — the Price Brief: single-screen pricing-intelligence document */}
      <Route path={rp('/agent/pricing/brief')} element={<PriceBriefPage />} />
      <Route path={rp('/agent/reviews')} element={<AgentReviewsPage />} />
      {/* CALENDAR_DIARY_DESIGN §3.3 — full diary page (wave 2) */}
      <Route path={rp('/agent/calendar')} element={<CalendarDiaryPage />} />
      {/* AREA_SCORING_DESIGN — the Area X-Ray: contribution-first scoring page */}
      <Route path={rp('/agent/areas/:slug/xray')} element={<AreaXRayPage />} />
      {/* AGT-APR-006 — my submitted reports (sold-price + comparables) */}
      <Route path={rp('/agent/pricing/reports')} element={<MySubmittedReportsPage />} />
      {/* Wave 5 WF-05 / WF-06 agent submitters (AGT-APR-004 / AGT-APR-005).
          Static /new routes MUST precede :reportId outcome routes. */}
      <Route path={rp('/reports/comparables/new')} element={<BadComparableReportPage />} />
      <Route path={rp('/comparables/:comparableId/report')} element={<BadComparableReportPage />} />
      <Route path={rp('/reports/prices/new')} element={<PriceReportPage />} />
      <Route path={rp('/agent/pricing/reports/new')} element={<PriceReportPage />} />
      {/* AGT-REC-002 comparable-report outcome + matrix-legacy alias */}
      <Route
        path={rp('/reports/comparables/:reportId/outcome')}
        element={<ComparableReportOutcomePage />}
      />
      <Route path={rp('/agent/comparable-reports/:id')} element={<ComparableReportOutcomePage />} />
      {/* AGT-REC-003 price-report outcome + matrix-legacy alias */}
      <Route path={rp('/reports/prices/:reportId/outcome')} element={<PriceReportOutcomePage />} />
      <Route path={rp('/agent/pricing/reports/:id/outcome')} element={<PriceReportOutcomePage />} />
      {/* Wave 0 drawer/tab destinations — alias legacy inbox path. */}
      <Route path={rp('/inbox')} element={<InboxPage />} />
      {/* AGT-REC-004 — application outcome (Wave 1). Deep-link target for WF-02.
          Must stay BEFORE /inbox/:conversationId so "applications" is not captured. */}
      <Route path={rp('/inbox/applications/:applicationId')} element={<ApplicationOutcomePage />} />
      {/* AGT-REC-006 — WF-31 ownership-transfer outcome. BEFORE /inbox/:conversationId. */}
      <Route path={rp('/inbox/ownership-transfers/:transferId')} element={<OwnershipTransferOutcomePage />} />
      <Route path={rp('/inbox/:conversationId')} element={<InboxConversationPage />} />
      <Route path={rp('/dashboard/inbox')} element={<InboxPage />} />
      <Route path={rp('/dashboard/inbox/:conversationId')} element={<InboxConversationPage />} />
      <Route path={rp('/applications/:applicationId')} element={<ApplicationOutcomePage />} />
      <Route path={rp('/agency/applications/:appId/status')} element={<ApplicationOutcomePage />} />
      <Route path={rp('/tasks')} element={<TasksPage />} />
      <Route path={rp('/contacts')} element={<ContactsPage />} />
      {/* Static /new/full and /:id/edit outrank /contacts/:id by specificity. */}
      <Route path={rp('/contacts/new/full')} element={<ContactFormPage />} />
      <Route path={rp('/contacts/:id')} element={<ContactDetailPage />} />
      <Route path={rp('/contacts/:id/edit')} element={<ContactFormPage />} />
      <Route path={rp('/contacts/:contactId/relationships')} element={<RelationshipsEditorPage />} />
      <Route path={rp('/opportunities')} element={<OpportunitiesPage />} />
      <Route path={rp('/opportunities/:id')} element={<OpportunityDetailPage />} />
      <Route path={rp('/analytics/crm')} element={<CrmAnalyticsPage />} />
      <Route path={rp('/marketing')} element={<MarketingDashboardPage />} />
      <Route path={rp('/journeys')} element={<CampaignsPage />} />
      <Route path={rp('/journeys/new')} element={<CampaignBuilderPage />} />
      <Route path={rp('/journeys/:id/edit')} element={<CampaignBuilderPage />} />
      <Route path={rp('/journeys/runs/:runId')} element={<JourneyRunInspectorPage />} />
      <Route path={rp('/campaigns/paid/new')} element={<PaidCampaignBuilderPage />} />
      <Route path={rp('/campaigns')} element={<Navigate to={paths.journeys} replace />} />
      <Route path={rp('/campaigns/new')} element={<Navigate to={paths.journeysNew} replace />} />
      <Route path={rp('/campaigns/:id')} element={<Navigate to={paths.journeys} replace />} />
      <Route path={rp('/audiences')} element={<AudiencesPage />} />
      <Route path={rp('/audiences/new')} element={<AudienceBuilderPage />} />
      <Route path={rp('/audiences/:id')} element={<AudienceBuilderPage />} />
      <Route path={rp('/experiments')} element={<ExperimentsPage />} />
      <Route path={rp('/experiments/new')} element={<ExperimentBuilderPage />} />
      <Route path={rp('/experiments/:id')} element={<ExperimentBuilderPage />} />
      <Route path={rp('/experiments/:id/results')} element={<ExperimentResultsPage />} />
      <Route path={rp('/message-templates')} element={<MessageTemplatesPage />} />
      <Route path={rp('/message-templates/new')} element={<MessageTemplateEditorPage />} />
      <Route path={rp('/message-templates/:id')} element={<MessageTemplateEditorPage />} />
      {mfaRoutes}
      <Route path={rp('/forgot-password')} element={<ForgotPasswordPage />} />
      <Route path={rp('/reset-password')} element={<ResetPasswordPage />} />
      <Route path={rp('/account-recovery')} element={<AccountRecoveryPage />} />
      <Route path={rp('/account-recovery/complete')} element={<AccountRecoveryCompletePage />} />
      <Route
        path={rp('/account/scheduled-deletion/:token')}
        element={<ScheduledDeletionConfirmationPage />}
      />
      <Route path={rp('/agency')} element={<AgencyManagementPage />} />
      {/* WF-31 ownership transfer (AGN-SET-005 initiator, AGN-SET-005b recipient). */}
      <Route path={rp('/agency/settings/ownership-transfer')} element={<AgencyOwnershipTransferInitiatorPage />} />
      <Route
        path={rp('/agency/ownership-transfer/incoming/:transferId')}
        element={<AgencyOwnershipTransferAcceptPage />}
      />
      <Route path={rp('/agency/members/applications')} element={<ApplicationsQueuePage />} />
      <Route
        path={rp('/agency/members/applications/:applicationId')}
        element={<ApplicationDetailPage />}
      />
      <Route path={rp('/agency/onboarding')} element={<AgencyOnboardingPage />} />
      <Route path={rp('/agency/public-profile')} element={<AgencyPublicProfileSettingsPage />} />
      <Route path={rp('/agency/settings')} element={<AgencySettingsHomePage />} />
      <Route path={rp('/agency/settings/roles')} element={<RolesOverviewPage />} />
      <Route path={rp('/agency/settings/roles/:packId')} element={<RolePermissionsDetailPage />} />
      <Route path={rp('/admin/trust')} element={<TrustCentreAdminPage />} />
      <Route path={rp('/admin/audit')} element={<AuditLogPage />} />
      <Route path={rp('/admin/audit/retention')} element={<AuditRetentionPolicyPage />} />
      <Route path={rp('/agency/settings/audit')} element={<AuditLogPage />} />
      <Route path={rp('/agency/settings/security')} element={<AgencySecurityPolicyPage />} />
      <Route path={rp('/agency/settings/branding')} element={<AgencyBrandingPage />} />
      <Route path={rp('/agency/settings/contact-policy')} element={<AgencyContactPolicyPage />} />
      <Route path={rp('/agency/routing')} element={<AgencyRoutingRulesPage />} />
      <Route path={rp('/agency/routing/rules/:ruleId')} element={<AgencyRoutingRuleEditorPage />} />
      <Route path={rp('/agency/templates')} element={<AgencyTemplatesListPage />} />
      <Route path={rp('/agency/templates/:templateId')} element={<AgencyTemplatesListPage />} />
      <Route path={rp('/agency/templates/:templateId')} element={<AgencyTemplateEditorPage />} />
      <Route path={rp('/agency/white-label/copy')} element={<AgencyWhiteLabelCopyPage />} />
      <Route path={rp('/agency/settings/delete-agency')} element={<AgencyDeleteAgencyPage />} />
      <Route path={rp('/agency/credits/allocation-rules')} element={<AgencyCreditAllocationRulesPage />} />
      <Route path={rp('/agency/pricing')} element={<AgencyPricingPage />} />
      <Route path={rp('/agency/pricing/bulk-adjust')} element={<AgencyBulkPriceAdjustPage />} />
      <Route path={rp('/agency/pricing/comparables')} element={<AgencyComparablesPage />} />
      <Route path={rp('/agency/reports/leads')} element={<LeadConversionFunnelPage />} />
      <Route path={rp('/agency/reports/revenue')} element={<RevenueAttributionReportPage />} />
      <Route path={rp('/agency/reports')} element={<AgencyReportsHomePage />} />
      <Route path={rp('/agency/reports/listings')} element={<AgencyListingsPerformanceReportPage />} />
      <Route path={rp('/agency/reports/agents')} element={<AgencyAgentLeaderboardPage />} />
      <Route path={rp('/agency/reports/campaigns')} element={<AgencyCampaignPerformanceReportPage />} />
      <Route path={rp('/agency/reports/attribution')} element={<AgencyAttributionPerformancePage />} />
      <Route path={rp('/agency/credits')} element={<AgencyCreditsLayout />}>
        <Route index element={<AgencyCreditsWalletPage />} />
        <Route path="quotas" element={<AgencyFeatureQuotasPage />} />
      </Route>
      <Route path={rp('/white-label')} element={<WhiteLabelBuilderPage />} />
      <Route path={rp('/agency/white-label/analytics')} element={<AgencyWhiteLabelAnalyticsPage />} />
      <Route path={rp('/widgets')} element={<WidgetBuilderPage />} />
      <Route path={rp('/plans')} element={<PlansPage />} />
      <Route path={rp('/my-subscription')} element={<MySubscriptionPage />} />
      <Route path={rp('/my-credits')} element={<MyCreditsPage />} />
      <Route path={rp('/my-credit-notes')} element={<MyCreditNotesPage />} />
      <Route path={rp('/my-invoices')} element={<MyInvoicesPage />} />
      <Route path={rp('/integrations')} element={<IntegrationSettingsPage />} />
      <Route path={rp('/settings')} element={<SettingsPage />}>
        {mfaSettingsChildRoutes}
        {settingsRoutes}
        <Route path="channels" element={<SocialChannelsPage />} />
        <Route path="routing" element={<RoutingSettingsPage />} />
        <Route path="historical-transactions" element={<HistoricalTransactionsPage />} />
      </Route>
      <Route path={rp('/command-center')} element={<CommandCenterPage />} />
      <Route path={rp('/operations')} element={<CommandCenterPage />} />
      <Route path={rp('/admin/whatsapp-listings')} element={<AdminWhatsAppListingsPage />} />
      <Route path={rp('/admin/whatsapp-listings/audit')} element={<WhatsAppAuditLogPage />} />
      <Route path={rp('/admin/notifications/dead-letter')} element={<NotificationDeadLetterPage />} />
      <Route path={rp('/admin/message-templates')} element={<PlatformMessageTemplatesPage />} />
      <Route path={rp('/admin/message-templates/new')} element={<PlatformTemplateEditPage />} />
      <Route path={rp('/admin/message-templates/:id')} element={<PlatformTemplateEditPage />} />
      <Route path={rp('/admin/areas')} element={<AdminAreasPage />} />
      <Route path={rp('/admin/areas/:areaId/signals')} element={<AreaSignalsReviewPage />} />
      <Route path={rp('/admin/areas/new')} element={<AdminAreaDetailPage mode="create" />} />
      <Route path={rp('/admin/areas/:id')} element={<AdminAreaDetailPage />} />
      <Route path={rp('/admin/scoring')} element={<AdminScoringPage />} />
      <Route path={rp('/admin/scoring/ai-configs')} element={<AiConfigsPage />} />
      <Route path={rp('/admin/comment-classifier')} element={<CommentClassifierPage />} />
      <Route path={rp('/admin/google-usage')} element={<GoogleUsagePage />} />
      <Route path={rp('/admin/pricing')} element={<PricingAdminPage />} />
      <Route path={rp('/admin/pricing/canonical')} element={<CanonicalResolutionQueuePage />} />
      <Route path={rp('/admin/pricing/canonical/:id')} element={<CanonicalResolutionDetailPage />} />
      <Route path={rp('/admin/support/account-recovery')} element={<AccountRecoveryQueuePage />} />
      {/* PA-ACR-002 - account recovery detail (cast-vote only; BE-BLOCKER-22) */}
      <Route path={rp('/admin/support/account-recovery/:caseId')} element={<AccountRecoveryDetailPage />} />
      <Route path={rp('/admin/moderation/portals')} element={<PortalModerationQueuePage />} />
      <Route
        path={rp('/admin/valuation/comparable-reports')}
        element={<BadComparableQueuePage />}
      />
      <Route
        path={rp('/admin/valuation/comparable-reports/:reportId')}
        element={<BadComparableDetailPage />}
      />
      <Route path={rp('/admin/valuation/price-reports')} element={<PriceReportQueuePage />} />
      <Route
        path={rp('/admin/valuation/price-reports/:reportId')}
        element={<PriceReportDetailPage />}
      />
      {/* PA-POR-001..003 — portal registry admin (list / add-edit / activation history) */}
      <Route path={rp('/admin/portals')} element={<PortalRegistryListPage />} />
      <Route path={rp('/admin/portals/new')} element={<PortalDetailPage mode="create" />} />
      <Route path={rp('/admin/portals/:code/edit')} element={<PortalDetailPage mode="edit" />} />
      <Route path={rp('/admin/portals/:code/history')} element={<PortalActivationHistoryPage />} />
      <Route path={rp('/admin/portals/:code')} element={<PortalDetailPage mode="view" />} />
      {/* Market on/off registry (migration 804) */}
      <Route path={rp('/admin/markets')} element={<MarketsAdminPage />} />
      {/* Strategy E-CFG platform defaults (F4) */}
      <Route path={rp('/admin/strategy/config')} element={<StrategyConfigAdminPage />} />
      {/* PA-PKG-001..004 — package admin (list / edit / approval queue+detail / version history) */}
      <Route path={rp('/admin/packages')} element={<PackageListPage />} />
      <Route path={rp('/admin/packages/approvals')} element={<PackageApprovalQueuePage />} />
      <Route path={rp('/admin/packages/approvals/:versionId')} element={<PackageApprovalDetailPage />} />
      <Route path={rp('/admin/packages/:packageId/history')} element={<PackageVersionHistoryPage />} />
      <Route path={rp('/admin/packages/:packageId/versions/:versionId/edit')} element={<PackageEditPage />} />
      <Route path={rp('/admin/packages/:packageId/versions/:version')} element={<PackageVersionReadOnlyPage />} />
      <Route path={rp('/admin/fin')} element={<Navigate to={paths.adminFinOverview} replace />} />
      <Route path={rp('/admin/fin/overview')} element={<OverviewPage />} />
      <Route path={rp('/admin/fin/tenants')} element={<TenantsPage />} />
      <Route path={rp('/admin/fin/tenants/:id')} element={<TenantDetailPage />} />
      <Route path={rp('/admin/fin/usage')} element={<UsagePage />} />
      <Route path={rp('/admin/fin/credits')} element={<CreditsPage />} />
      <Route path={rp('/admin/fin/credits/lots')} element={<CreditLotsPage />} />
      <Route path={rp('/admin/fin/credits/janitor')} element={<CreditJanitorPage />} />
      <Route path={rp('/admin/fin/credits/fin-mirror')} element={<CreditFinMirrorPage />} />
      <Route path={rp('/admin/fin/holds')} element={<HoldsPage />} />
      <Route path={rp('/admin/fin/facilities')} element={<FacilitiesPage />} />
      <Route path={rp('/admin/fin/contracts')} element={<ContractsPage />} />
      <Route path={rp('/admin/fin/contracts/:id')} element={<ContractDetailPage />} />
      <Route path={rp('/admin/fin/contracts/:contractId/versions/new')} element={<ContractVersionEditorPage />} />
      <Route path={rp('/admin/fin/pricing')} element={<FinPricingPage />} />
      <Route path={rp('/admin/fin/pricing/:id')} element={<PriceDetailPage />} />
      <Route path={rp('/admin/fin/packages')} element={<PackagesPage />} />
      <Route path={rp('/admin/fin/packages/features')} element={<FeatureRegistryPage />} />
      <Route path={rp('/admin/fin/packages/:id')} element={<PackageDetailPage />} />
      <Route path={rp('/admin/fin/packages/:id/versions/:vid')} element={<PackageVersionEditor />} />
      <Route path={rp('/admin/fin/package-approvals')} element={<PackageApprovalPage />} />
      <Route path={rp('/admin/fin/subscriptions')} element={<SubscriptionsPage />} />
      <Route path={rp('/admin/fin/subscriptions/:id')} element={<SubscriptionDetailPage />} />
      <Route path={rp('/admin/fin/invoices')} element={<InvoicesPage />} />
      <Route path={rp('/admin/fin/dunning')} element={<DunningCasesPage />} />
      <Route path={rp('/admin/fin/payments')} element={<PaymentsPage />} />
      <Route path={rp('/admin/fin/vendor-costs')} element={<VendorCostsPage />} />
      <Route path={rp('/admin/fin/vendors/:vendorId/statements/:month')} element={<VendorStatementDetailPage />} />
      <Route path={rp('/admin/fin/vendors/:id')} element={<VendorDetailPage />} />
      <Route path={rp('/admin/fin/reconciliation')} element={<ReconciliationPage />} />
      <Route path={rp('/admin/fin/reconciliation/:id')} element={<ReconciliationRunDetailPage />} />
      <Route path={rp('/admin/fin/accounting/periods')} element={<AccountingPeriodsPage />} />
      <Route path={rp('/admin/fin/accounting/periods/:id')} element={<AccountingPeriodDetailPage />} />
      <Route path={rp('/admin/fin/dunning/:id')} element={<DunningCaseDetailPage />} />
      <Route path={rp('/admin/fin/exceptions')} element={<ExceptionsPage />} />
      <Route path={rp('/admin/fin/exceptions/:id')} element={<ExceptionDetailPage />} />
      <Route path={rp('/admin/fin/approvals')} element={<ApprovalsPage />} />
      <Route path={rp('/admin/fin/approvals/:id/audit')} element={<ApprovalAuditTrailPage />} />
      <Route path={rp('/admin/fin/audit')} element={<AuditPage />} />
      <Route path={rp('/admin/fin/dunning/:id/advance')} element={<AdvanceDunningStagePage />} />
      <Route path={rp('/admin/fin/dunning/:id/cure')} element={<CureDunningCasePage />} />
      <Route path={rp('/admin/fin/dunning/:id/write-off')} element={<WriteOffDunningCasePage />} />
      <Route path={rp('/admin/fin/configuration')} element={<ConfigurationPage />} />
      <Route path={rp('/admin/fin/billing/periods/:id/close')} element={<BillingPeriodClosePage />} />
      <Route path={rp('/notifications')} element={<NotificationsInboxPage />} />
      <Route path={rp('/notification-preferences')} element={<NotificationPreferencesPage />} />
      <Route path={rp('/agency/whatsapp-listings')} element={<AgencyWhatsAppListingsPage />} />
      <Route path={rp('/agent/whatsapp-listings')} element={<AgentWhatsAppListingsPage />} />
      <Route path={rp('/agent/whatsapp-listings/settings')} element={<WhatsAppIntakeSettingsPage />} />
      <Route
        path={rp('/agent/whatsapp-listings/analytics')}
        element={
          <Suspense
            fallback={
              <div
                className="flex min-h-[50vh] items-center justify-center text-[var(--lc-text-muted)]"
                role="status"
              >
                Loading analytics…
              </div>
            }
          >
            <WhatsAppIntakeAnalyticsPage />
          </Suspense>
        }
      />
      {whatsappIntakeRoutes}
      <Route path={rp('/areas/:slug')} element={<AreaProfilePage />} />
      <Route path={rp('/inspector')} element={<InspectorPage />} />
      <Route path={rp('/inspector/:id/submit')} element={<InspectorSubmitPage />} />
      <Route path={rp('/public/agency/:id')} element={<PublicAgencyPage />} />
      <Route path={rp('/public/agent/:id')} element={<PublicAgentPortfolioPage />} />
      <Route path={rp('/public/relationships/consent')} element={<RelationshipConsentPage />} />
      {/* Calendar wave 6a — anonymous booking page; token-only, no auth guard. */}
      <Route path={rp('/book/:token')} element={<PublicBookingPage />} />
      <Route path={rp('/r/:shareToken')} element={<SellerReportSharePage />} />
      <Route path={rp('/agencies/:agencySlug/apply')} element={<PublicAgencyApplyPage />} />
      <Route path={rp('/join/:invitationCode')} element={<PublicAgencyApplyPage />} />
      <Route path={rp('/site/:subdomain')} element={<PublicWhiteLabelSitePage />} />
      <Route path={rp('/site/:subdomain/property/:propertyId')} element={<PublicWhiteLabelPropertyPage />} />
      <Route path={rp('/terms')} element={<TermsPage />} />
      <Route path={rp('/privacy')} element={<PrivacyPage />} />
      {/* Trust Centre (BUILD-10). Public, alongside the other legal surfaces —
          /trust is linked from security.txt and read by prospects who have never
          signed in, so it must not sit behind the authed shell. */}
      <Route path={rp('/trust')} element={<TrustCentrePage />} />
      <Route path={rp('/trust/vulnerability-disclosure')} element={<TrustDisclosurePage />} />
      <Route path={rp('/trust/subprocessors/confirm')} element={<TrustNoticeActionPage mode="confirm" />} />
      <Route path={rp('/trust/subprocessors/unsubscribe')} element={<TrustNoticeActionPage mode="unsubscribe" />} />
      <Route path={rp('/refund')} element={<RefundPolicyPage />} />
      <Route path={rp('/cookies')} element={<CookieNoticePage />} />
      {onboardingRoutes}
      {activationRoutes}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Sentry.ErrorBoundary>
  )
}

function AppShell() {
  const location = useLocation()
  const { agent, loading } = useAuth()
  const bare = usesBareChrome(location.pathname)

  // SHR-ERR-005 — platform maintenance / degraded strip, mounted once above
  // every surface (authed personas, guest chrome, and bare auth pages). It
  // renders nothing while the platform is healthy, so there is no layout cost
  // in the common case.
  const statusBanner = <PlatformStatusBanner />

  if (bare) {
    return (
      <>
        {statusBanner}
        <AppRoutes />
      </>
    )
  }

  // Signed-in: Wave 0 persona chrome (agent / agency / pa).
  if (!loading && agent) {
    return (
      <>
        {statusBanner}
        <PersonaAppShell>
          <AppRoutes />
        </PersonaAppShell>
      </>
    )
  }

  // Guest / auth-loading: legacy public Navbar + Footer.
  return (
    <div className="flex min-h-screen flex-col">
      {statusBanner}
      <Navbar />
      <main className="flex-1">
        <AppRoutes />
      </main>
      <Footer />
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <BrandProvider>
        <ToastProvider>
          <AuthProvider>
            {/* Inside AuthProvider: step-up acts on the current session. */}
            <StepUpProvider>
              <MfaStepUpProvider>
                <AppShell />
              </MfaStepUpProvider>
            </StepUpProvider>
          </AuthProvider>
        </ToastProvider>
      </BrandProvider>
    </ErrorBoundary>
  )
}

export default App
